import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';
import { AnalyticsService } from '$lib/services/analytics/index.js';
import { IS_LOCAL_DEVELOPMENT, SECURE } from '$lib/utils/cookies-config.js';
import { getDeviceId } from '$lib/utils/device-id.js';
import { UAParser } from 'ua-parser-js';

const EVENT_TYPE = 'cookie_debug';

const DATA_WRITE_TIME_KEY = '_aoflcttv2';

// Test localStorage key used by testLocalStorageAvailability
const TEST_LOCAL_STORAGE_KEY = '_lst';

const COOKIE_TEST_CASES = {
	// test if cookie name is the issue, not uuid value
	wdyjwu: {
		value: uuidv4,
		expires: 399
	},
	// test if both name and value cause issue
	wdyjwr: {
		value: Math.random,
		expires: 399
	},
	// test if duration is the issue by using a shorter expiration
	device_id_: {
		value: uuidv4,
		expires: 7
	},
	// test if duration is the issue by using a shorter expiration
	// test if the value is causing issue by using a random number
	device_id__: {
		value: Math.random,
		expires: 399
	},
	// test if duration is the issue by using a shorter expiration
	device_id___: {
		value: uuidv4,
		expires: 28
	}
};

const readData = async () => {
	const result = {
		success: true,
		cookies: {},
		localStorage: {},
		lastWrittenTime: localStorage.getItem(DATA_WRITE_TIME_KEY)
	};

	for (const name of Object.keys(COOKIE_TEST_CASES)) {
		try {
			const cookieValue = Cookies.get(name);
			result.cookies[name] = cookieValue;
			if (!cookieValue || cookieValue === 'null') {
				result.success = false;
			}
		} catch (e) {
			result.success = false;
			result.cookies[name] = `Error: ${e}`;
		}

		try {
			const cookieValue = Cookies.get(name + '_strict');
			result.cookies[name + '_strict'] = cookieValue;
			if (!cookieValue || cookieValue === 'null') {
				result.success = false;
			}
		} catch (e) {
			result.success = false;
			result.cookies[name + '_strict'] = `Error: ${e}`;
		}

		try {
			const localStorageValue = localStorage.getItem(name);
			result.localStorage[name] = localStorageValue;
			if (!localStorageValue || localStorageValue === 'null') {
				result.success = false;
			}
		} catch (e) {
			result.success = false;
			result.localStorage[name] = `Error: ${e}`;
		}
	}

	return result;
};

const writeData = () => {
	for (const [name, config] of Object.entries(COOKIE_TEST_CASES)) {
		const value = config.value();

		Cookies.set(name, value, {
			secure: SECURE,
			expires: config.expires, // days
			path: '/',
			domain: IS_LOCAL_DEVELOPMENT ? '' : '.abcmouse.com',
			sameSite: /** @type {'lax'} */ ('lax')
		});

		Cookies.set(name + '_strict', value, {
			secure: SECURE,
			expires: config.expires, // days
			path: '/',
			domain: IS_LOCAL_DEVELOPMENT ? '' : '.abcmouse.com',
			sameSite: /** @type {'strict'} */ ('strict')
		});

		localStorage.setItem(name, value);
	}
	const now = Date.now().toString();
	localStorage.setItem(DATA_WRITE_TIME_KEY, now);
};

export const runCookieTests = async () => {
	const nowInSec = Date.now() / 1000;
	if (nowInSec > 1771146378) {
		// Sun Feb 15 2026 01:06:18 GMT-0800 (Pacific Standard Time)
		return;
	}

	const [result, duration] = await profileTime(async () => {
		if (!localStorage.getItem(DATA_WRITE_TIME_KEY)) {
			writeData();
		}

		return await readData();
	});

	const ua = await UAParser().withFeatureCheck();

	result.ua = ua;
	result.deviceId = getDeviceId();
	result.duration = duration;

	if (!result.success) {
		AnalyticsService.trackClientDebug({
			event_subtype: EVENT_TYPE,
			result
		});
	}
};

export const testLocalStorageAvailability = async () => {
	const nowInSec = Date.now() / 1000;
	if (nowInSec > 1771146378) {
		// Sun Feb 15 2026 01:06:18 GMT-0800 (Pacific Standard Time)
		return;
	}

	let attempt = 0;

	const value = '5380cb78-8958-4297-9a21-243791e62dcd';
	let success = false;

	do {
		localStorage.setItem(TEST_LOCAL_STORAGE_KEY, value);
		if (localStorage.getItem(TEST_LOCAL_STORAGE_KEY) === value) {
			success = true;
			break;
		}

		attempt++;
		await sleep(1000);
	} while (attempt < 5);

	if (attempt > 0) {
		await AnalyticsService.trackClientDebug({
			event_subtype: EVENT_TYPE,
			localStorageAttempt: attempt,
			success
		});
	}
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const profileTime = async (fn) => {
	const start = performance.now();
	const result = await fn();
	const end = performance.now();
	const timeElapsedInMs = end - start;

	return [result, timeElapsedInMs];
};

/**
 * Get all test cookie names (including _strict variants)
 * @returns {string[]} Array of test cookie names
 */
export const getTestCookieNames = () => {
	const cookieNames = Object.keys(COOKIE_TEST_CASES);
	const strictCookieNames = cookieNames.map((name) => `${name}_strict`);
	return [...cookieNames, ...strictCookieNames];
};

/**
 * Get all test localStorage keys
 * @returns {string[]} Array of test localStorage keys
 */
export const getTestLocalStorageKeys = () => {
	const cookieTestKeys = Object.keys(COOKIE_TEST_CASES);
	return [...cookieTestKeys, DATA_WRITE_TIME_KEY, TEST_LOCAL_STORAGE_KEY];
};
